import {
  HHAlert,
  HHBox,
  HHCircularProgress,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { FileProcessingStatus } from '../domain/enums';
import { useIngestionBatches } from '../hooks/use-ingestion-batches';
import { BatchHeatmap } from './batch-heatmap';
import { BatchTable } from './batch-table';
import { BatchTotalsBar } from './batch-totals-bar';
import { CursorPagination } from './cursor-pagination';

export const PARSING_TAB_TEST_ID = 'ingestion-parsing-tab';
export const PARSING_TAB_LOADING_TEST_ID = 'ingestion-parsing-tab-loading';
export const PARSING_TAB_ERROR_TEST_ID = 'ingestion-parsing-tab-error';
export const PARSING_TAB_STALE_WARNING_TEST_ID =
  'ingestion-parsing-tab-stale-warning';

export interface ParsingTabProps {
  eventId: string;
  /**
   * Status of the parent ingestion, passed down from the detail page. Batch
   * rows have their own lifecycle, so this is the only signal that tells the
   * tab when to stop polling for changes.
   */
  parentStatus: FileProcessingStatus;
  /** Test seam: set to `0` to disable polling. */
  pollIntervalMs?: number;
}

/**
 * Parsing tab of the ingestion detail page: how the file was split into
 * byte-range batches and how each one fared.
 */
export const ParsingTab = ({
  eventId,
  parentStatus,
  pollIntervalMs,
}: ParsingTabProps): JSX.Element => {
  const {
    batches,
    totals,
    isLoading,
    hasError,
    hasNext,
    hasPrevious,
    currentPage,
    goToNext,
    goToPrevious,
  } = useIngestionBatches(eventId, { parentStatus, pollIntervalMs });

  const hasLoadedBatches = batches.length > 0;

  if (isLoading && !hasLoadedBatches) {
    return (
      <HHBox
        data-testid={PARSING_TAB_LOADING_TEST_ID}
        display="flex"
        justifyContent="center"
        paddingY={4}
      >
        <HHCircularProgress />
      </HHBox>
    );
  }

  if (hasError && !hasLoadedBatches) {
    return (
      // testid sits on the wrapper: HHAlert does not forward unknown props to
      // the DOM.
      <HHBox data-testid={PARSING_TAB_ERROR_TEST_ID} paddingY={4}>
        <HHAlert hhVariant="outlined" severity="error">
          Unable to load parsing batches. Please try again.
        </HHAlert>
      </HHBox>
    );
  }

  return (
    <HHStack gap={4} data-testid={PARSING_TAB_TEST_ID}>
      {/* A poll failed but the last good page is still on screen, so warn
          rather than replacing it with an error. */}
      {hasError && (
        <HHBox data-testid={PARSING_TAB_STALE_WARNING_TEST_ID}>
          <HHAlert hhVariant="outlined" severity="warning">
            Live updates paused — showing the last successful snapshot.
            Retrying…
          </HHAlert>
        </HHBox>
      )}

      <BatchTotalsBar totals={totals} />

      <HHStack gap={1}>
        <HHTypography hhVariant="variant-bypass" variant="h3">
          Batch Heatmap
        </HHTypography>
        <BatchHeatmap batches={batches} />
      </HHStack>

      <BatchTable batches={batches} />

      <CursorPagination
        currentPage={currentPage}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        onNext={goToNext}
        onPrevious={goToPrevious}
      />
    </HHStack>
  );
};
